import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Send, Inbox, SendHorizontal, Trash2, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { format } from "date-fns";

export default function Messages() {
  const queryClient = useQueryClient();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isComposeDialogOpen, setIsComposeDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [formData, setFormData] = useState({
    recipient_id: "broadcast",
    subject: "",
    content: "",
    priority: "normal",
  });

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    fetchUser();
  }, []);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!currentUserId) return;

    const channel = supabase
      .channel("messages-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["messages"] });
          queryClient.invalidateQueries({ queryKey: ["unread-count"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, queryClient]);

  const { data: messages, isLoading } = useQuery({
    queryKey: ["messages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!currentUserId,
  });

  const { data: unreadCount } = useQuery({
    queryKey: ["unread-count"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages" as any)
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", currentUserId)
        .eq("is_read", false);
      if (error) throw error;
      return data;
    },
    enabled: !!currentUserId,
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles" as any)
        .select("id, name, email");
      if (error) throw error;
      return data;
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (message: typeof formData) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from("profiles" as any)
        .select("name")
        .eq("id", user?.id)
        .single();

      const { data: recipientProfile } = await supabase
        .from("user_roles" as any)
        .select("role")
        .eq("user_id", message.recipient_id)
        .maybeSingle();

      const { error } = await supabase
        .from("messages" as any)
        .insert([{
          sender_id: user?.id,
          sender_name: (profile as any)?.name || "Unknown",
          sender_role: (recipientProfile as any)?.role || null,
          recipient_id: message.recipient_id === "broadcast" ? null : message.recipient_id,
          subject: message.subject,
          content: message.content,
          priority: message.priority,
        }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message sent successfully");
      setIsComposeDialogOpen(false);
      setFormData({
        recipient_id: "broadcast",
        subject: "",
        content: "",
        priority: "normal",
      });
    },
    onError: (error) => {
      toast.error("Failed to send message: " + error.message);
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from("messages" as any)
        .update({ is_read: true })
        .eq("id", messageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["unread-count"] });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from("messages" as any)
        .delete()
        .eq("id", messageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message deleted");
      setSelectedMessage(null);
    },
  });

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive"; icon: any }> = {
      low: { variant: "secondary", icon: <Clock className="h-3 w-3" /> },
      normal: { variant: "default", icon: <CheckCircle2 className="h-3 w-3" /> },
      high: { variant: "default", icon: <AlertCircle className="h-3 w-3" /> },
      urgent: { variant: "destructive", icon: <AlertCircle className="h-3 w-3" /> },
    };
    return variants[priority] || variants.normal;
  };

  const inbox = messages?.filter((m: any) => m.recipient_id === currentUserId) || [];
  const sent = messages?.filter((m: any) => m.sender_id === currentUserId) || [];

  const MessageList = ({ messageList, isSent }: { messageList: any[]; isSent: boolean }) => (
    <ScrollArea className="h-[600px]">
      <div className="space-y-2 pr-4">
        {messageList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No messages found
          </div>
        ) : (
          messageList.map((message: any) => {
            const priorityInfo = getPriorityBadge(message.priority);
            return (
              <Card
                key={message.id}
                className={`cursor-pointer transition-colors hover:bg-accent ${
                  !message.is_read && !isSent ? "border-primary bg-primary/5" : ""
                }`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.is_read && !isSent) {
                    markAsReadMutation.mutate(message.id);
                  }
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className="text-base">
                          {isSent ? "To: " : "From: "}
                          {isSent
                            ? (users as any)?.find((u: any) => u.id === message.recipient_id)?.name || "All Users"
                            : message.sender_name}
                        </CardTitle>
                        {!message.is_read && !isSent && (
                          <Badge variant="default" className="text-xs">New</Badge>
                        )}
                      </div>
                      <CardDescription className="font-medium">{message.subject}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={priorityInfo.variant} className="flex items-center gap-1">
                        {priorityInfo.icon}
                        {message.priority}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(message.created_at), "MMM d, h:mm a")}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {message.content}
                  </p>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Messages</h1>
            <p className="text-muted-foreground">
              Internal communication hub for compliance team
            </p>
          </div>
          <Dialog open={isComposeDialogOpen} onOpenChange={setIsComposeDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Compose Message
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Compose New Message</DialogTitle>
                <DialogDescription>
                  Send a message to another user or broadcast to all users
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient">Recipient</Label>
                  <Select
                    value={formData.recipient_id}
                    onValueChange={(value) => setFormData({ ...formData, recipient_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select recipient or broadcast to all" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="broadcast">All Users (Broadcast)</SelectItem>
                      {users?.map((user: any) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Message subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Message *</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Type your message here..."
                    rows={6}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsComposeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => sendMessageMutation.mutate(formData)}
                  disabled={!formData.subject || !formData.content || sendMessageMutation.isPending}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send Message
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Tabs defaultValue="inbox">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="inbox" className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" />
                  Inbox
                  {(unreadCount as any) > 0 && (
                    <Badge variant="destructive" className="ml-1">
                      {(unreadCount as any)}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="sent" className="flex items-center gap-2">
                  <SendHorizontal className="h-4 w-4" />
                  Sent
                </TabsTrigger>
              </TabsList>
              <TabsContent value="inbox" className="mt-4">
                {isLoading ? (
                  <div className="text-center py-12">Loading messages...</div>
                ) : (
                  <MessageList messageList={inbox} isSent={false} />
                )}
              </TabsContent>
              <TabsContent value="sent" className="mt-4">
                {isLoading ? (
                  <div className="text-center py-12">Loading messages...</div>
                ) : (
                  <MessageList messageList={sent} isSent={true} />
                )}
              </TabsContent>
            </Tabs>
          </div>

          <div>
            {selectedMessage ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{selectedMessage.subject}</CardTitle>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>
                          <span className="font-medium">From:</span> {selectedMessage.sender_name}
                        </p>
                        {selectedMessage.recipient_id && (
                          <p>
                            <span className="font-medium">To:</span>{" "}
                            {(users as any)?.find((u: any) => u.id === selectedMessage.recipient_id)?.name || "Unknown"}
                          </p>
                        )}
                        <p>
                          <span className="font-medium">Date:</span>{" "}
                          {format(new Date(selectedMessage.created_at), "MMM d, yyyy h:mm a")}
                        </p>
                      </div>
                    </div>
                    <Badge variant={getPriorityBadge(selectedMessage.priority).variant}>
                      {selectedMessage.priority}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                  </div>
                  {selectedMessage.sender_id === currentUserId && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteMessageMutation.mutate(selectedMessage.id)}
                      className="w-full"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Message
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="text-center py-12 text-muted-foreground">
                  Select a message to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
